import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import IsoDatePicker from "../components/IsoDatePicker";
import {
  confirmRoomHold,
  createRoomHold,
  fetchRoomHolds,
  releaseRoomHold,
} from "../services/hotelAvailabilityService";
import { formatDateDMY } from "../utils/dateFormat";

const pad = (value) => String(value).padStart(2, "0");
const isoDate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const todayValue = () => isoDate(new Date());
const addDays = (dateValue, days) => {
  const date = new Date(`${dateValue}T00:00:00`);
  date.setDate(date.getDate() + days);
  return isoDate(date);
};
const nightsBetween = (start, end) => {
  const nights = [];
  for (let night = start; night && end && night < end; night = addDays(night, 1)) nights.push(night);
  return nights;
};

const whatsappNumber = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `91${digits.slice(1)}`;
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  return "";
};

const copyText = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  window.prompt("Copy this message:", text);
};

const AGREEMENT_LABELS = {
  AWAITING_HOTEL: { text: "Awaiting hotel", bg: "warning", textColor: "dark" },
  HOTEL_ACCEPTED: { text: "Hotel accepted", bg: "success" },
  CONFIRMED_BY_US: { text: "Confirmed by us", bg: "info", textColor: "dark" },
};

const nightsLabel = (count) => `${count} night${count === 1 ? "" : "s"}`;

/** One message per hotel and date range, covering every room type held there. */
const holdMessage = (holds) => {
  const first = holds[0];
  const nights = nightsBetween(first.startDate, first.endDate).length;
  const greeting = first.caretakerName ? `Hello ${first.caretakerName},` : "Hello,";
  return [
    greeting,
    "",
    `Iotrips would like you to *hold these rooms for us* at ${first.hotelName || first.hotelCode}:`,
    ...holds.map((hold) => `• ${hold.categoryName} - ${hold.units} room${hold.units === 1 ? "" : "s"}`),
    "",
    `Check-in: ${formatDateDMY(first.startDate)}`,
    `Check-out: ${formatDateDMY(first.endDate)} (${nightsLabel(nights)})`,
    first.note ? `Note: ${first.note}` : "",
    "",
    "Please do not give these rooms to other guests. We will tell you when a room is no longer needed.",
    "Please confirm. Thank you!",
  ].filter((line, index, lines) => line !== "" || lines[index - 1] !== "").join("\n");
};

const usageText = (hold) => {
  if (hold.status !== "ACTIVE") return "Released";
  const nights = hold.nights || [];
  const allUnused = nights.every((night) => night.unused === hold.units);
  if (allUnused) return `All ${hold.units} unused`;
  const allUsed = nights.every((night) => night.unused === 0);
  if (allUsed) return `All ${hold.units} used`;
  return nights.map((night) => `${formatDateDMY(night.date).slice(0, 5)}: ${night.unused} unused`).join(", ");
};

/**
 * Rooms held for Iotrips ahead of a busy period. Holds take effect as soon as they are created and
 * are never released automatically - the list puts the ones needing a decision first instead.
 */
export default function RoomHoldsManager({ hotels, createForHotel, onCloseCreate, onChanged }) {
  const [holds, setHolds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [showReleased, setShowReleased] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setHolds(await fetchRoomHolds(showReleased));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load room holds");
    } finally {
      setLoading(false);
    }
  }, [showReleased]);

  useEffect(() => {
    load();
  }, [load]);

  const sortedHolds = useMemo(() => holds.slice().sort((left, right) => {
    const rank = (hold) => (hold.status !== "ACTIVE" ? 2 : hold.needsAttention ? 0 : 1);
    return rank(left) - rank(right)
      || String(left.startDate).localeCompare(String(right.startDate))
      || String(left.hotelCode || "").localeCompare(String(right.hotelCode || ""));
  }), [holds]);

  const activeCount = holds.filter((hold) => hold.status === "ACTIVE").length;
  const attentionCount = holds.filter((hold) => hold.needsAttention).length;
  const awaitingCount = holds.filter((hold) => hold.status === "ACTIVE" && hold.hotelAgreement === "AWAITING_HOTEL").length;

  const afterChange = async () => {
    await load();
    onChanged?.();
  };

  const release = async (hold) => {
    const unused = hold.unusedRoomNights || 0;
    const message = unused > 0
      ? `Release the hold on ${hold.categoryName} at ${hold.hotelCode}?\n\n${unused} unused room-night${unused === 1 ? "" : "s"} go back to the hotel. Rooms already used by bookings stay with those bookings.`
      : `End the hold on ${hold.categoryName} at ${hold.hotelCode}? All its rooms are already used by bookings.`;
    if (!window.confirm(message)) return;
    setBusyId(hold.id);
    try {
      await releaseRoomHold(hold.id);
      toast.success("Hold released");
      await afterChange();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to release hold");
    } finally {
      setBusyId(null);
    }
  };

  const markConfirmed = async (hold) => {
    setBusyId(hold.id);
    try {
      await confirmRoomHold(hold.id);
      toast.success("Marked as confirmed with the hotel");
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update hold");
    } finally {
      setBusyId(null);
    }
  };

  const holdsLikeThis = (hold) => holds.filter((item) =>
    item.status === "ACTIVE"
    && item.hotelId === hold.hotelId
    && item.startDate === hold.startDate
    && item.endDate === hold.endDate);

  const copyMessage = async (hold) => {
    await copyText(holdMessage(holdsLikeThis(hold)));
    toast.success("Hold message copied");
  };

  return (
    <Card className="mb-3 shadow-sm">
      <Card.Header
        className="d-flex flex-wrap justify-content-between align-items-center gap-2"
        role="button"
        onClick={() => setExpanded((current) => !current)}
      >
        <div>
          <div className="fw-semibold">
            <i className={`bi ${expanded ? "bi-chevron-down" : "bi-chevron-right"} me-2`} aria-hidden="true" />
            Room Holds for Iotrips
            {activeCount > 0 && <Badge bg="primary" className="ms-2">{activeCount} active</Badge>}
            {attentionCount > 0 && <Badge bg="danger" className="ms-1">{attentionCount} need a decision</Badge>}
            {awaitingCount > 0 && <Badge bg="warning" text="dark" className="ms-1">{awaitingCount} awaiting hotel</Badge>}
          </div>
          <div className="small text-muted">
            Rooms the hotel keeps for us. Bookings use them first. Nothing is released until you release it.
          </div>
        </div>
        <Form.Check
          type="switch"
          id="room-holds-show-released"
          label="Show released"
          checked={showReleased}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => setShowReleased(event.target.checked)}
        />
      </Card.Header>
      {expanded && (
        <Card.Body className="p-0">
          {loading && <div className="p-3"><Spinner animation="border" size="sm" /> Loading room holds...</div>}
          {!loading && sortedHolds.length === 0 && (
            <Alert variant="light" className="m-3 mb-3">
              No rooms are held. Use <strong>Hold Rooms</strong> on a hotel below to hold rooms for a busy period.
            </Alert>
          )}
          {!loading && sortedHolds.length > 0 && (
            <Table responsive hover className="mb-0 align-middle small">
              <thead>
                <tr>
                  <th>Hotel</th>
                  <th>Room Type</th>
                  <th>Dates</th>
                  <th>Held</th>
                  <th>Still unused</th>
                  <th>Hotel</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sortedHolds.map((hold) => {
                  const agreement = AGREEMENT_LABELS[hold.hotelAgreement] || AGREEMENT_LABELS.AWAITING_HOTEL;
                  const active = hold.status === "ACTIVE";
                  const nights = (hold.nights || []).length;
                  const number = whatsappNumber(hold.caretakerNumber);
                  return (
                    <tr key={hold.id} className={hold.needsAttention ? "table-danger" : !active ? "text-muted" : ""}>
                      <td>
                        <div className="fw-semibold">{hold.hotelCode}</div>
                        <div className="text-muted">{hold.city}</div>
                      </td>
                      <td>{hold.categoryName}</td>
                      <td className="text-nowrap">
                        <div>{formatDateDMY(hold.startDate)} → {formatDateDMY(hold.endDate)}</div>
                        <div className="text-muted">
                          {nightsLabel(nights)}
                          {active && hold.daysToCheckIn >= 0 ? ` · check-in in ${hold.daysToCheckIn} day${hold.daysToCheckIn === 1 ? "" : "s"}` : ""}
                          {active && hold.daysToCheckIn < 0 ? " · stay in progress" : ""}
                        </div>
                        {hold.note && <div className="text-muted fst-italic">{hold.note}</div>}
                      </td>
                      <td className="fw-semibold">{hold.units} room{hold.units === 1 ? "" : "s"}</td>
                      <td>
                        <div className={hold.needsAttention ? "text-danger fw-semibold" : ""}>{usageText(hold)}</div>
                        {hold.needsAttention && <div className="text-danger">Check-in is close - keep or release?</div>}
                      </td>
                      <td>
                        {active ? <Badge bg={agreement.bg} text={agreement.textColor}>{agreement.text}</Badge> : <Badge bg="secondary">Released</Badge>}
                        {hold.hotelAgreement === "HOTEL_ACCEPTED" && hold.hotelAcceptedByName && (
                          <div className="small mt-1">
                            By <strong>{hold.hotelAcceptedByName}</strong>
                            {hold.hotelAcceptedLatitude != null && hold.hotelAcceptedLongitude != null && (
                              <>
                                {" · "}
                                <a
                                  href={`https://www.google.com/maps?q=${hold.hotelAcceptedLatitude},${hold.hotelAcceptedLongitude}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <i className="bi bi-geo-alt-fill me-1" aria-hidden="true" />Map
                                </a>
                              </>
                            )}
                          </div>
                        )}
                        {active && hold.hotelAgreement === "AWAITING_HOTEL" && (
                          <div className="mt-1">
                            <Button size="sm" variant="link" className="p-0" disabled={busyId === hold.id} onClick={() => markConfirmed(hold)}>
                              Hotel confirmed by phone
                            </Button>
                          </div>
                        )}
                      </td>
                      <td className="text-nowrap">
                        {active && (
                          <div className="d-flex flex-column gap-1 align-items-start">
                            <div className="d-flex gap-1">
                              <Button size="sm" variant="outline-primary" onClick={() => copyMessage(hold)}>
                                <i className="bi bi-clipboard-check me-1" aria-hidden="true" />Copy
                              </Button>
                              {number && (
                                <Button
                                  as="a"
                                  size="sm"
                                  variant="success"
                                  href={`https://wa.me/${number}?text=${encodeURIComponent(holdMessage(holdsLikeThis(hold)))}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <i className="bi bi-whatsapp" aria-hidden="true" />
                                </Button>
                              )}
                            </div>
                            <Button size="sm" variant="outline-danger" disabled={busyId === hold.id} onClick={() => release(hold)}>
                              {busyId === hold.id ? <Spinner animation="border" size="sm" /> : "Release"}
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      )}
      <CreateHoldModal
        hotel={createForHotel}
        hotels={hotels}
        onHide={onCloseCreate}
        onCreated={async () => {
          onCloseCreate?.();
          setExpanded(true);
          await afterChange();
        }}
      />
    </Card>
  );
}

function CreateHoldModal({ hotel, hotels, onHide, onCreated }) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [note, setNote] = useState("");
  const [confirmedByUs, setConfirmedByUs] = useState(false);
  const [units, setUnits] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!hotel) return;
    setStartDate("");
    setEndDate("");
    setNote("");
    setConfirmedByUs(false);
    setUnits({});
  }, [hotel]);

  // Latest loaded availability for this hotel - only covers the dates on screen, so outside that
  // window the free count is unknown here and the server checks it on save.
  const liveHotel = useMemo(
    () => (hotels || []).find((item) => hotel && String(item.hotelId) === String(hotel.hotelId)) || hotel,
    [hotels, hotel],
  );
  const nights = nightsBetween(startDate, endDate);

  const freeRooms = (category) => {
    if (!nights.length) return null;
    const days = new Map((category.days || []).map((day) => [day.date, day]));
    if (!nights.every((night) => days.has(night))) return null;
    return Math.min(...nights.map((night) => Number(days.get(night).availableUnits || 0)));
  };

  const categories = liveHotel?.roomCategories || [];
  const holdWholeHotel = () => {
    const next = {};
    categories.forEach((category) => {
      const free = freeRooms(category);
      if (free != null && free > 0) next[category.roomCategoryId] = free;
    });
    if (!Object.keys(next).length) {
      toast.info("Pick dates shown on the availability table first - free rooms are read from there.");
      return;
    }
    setUnits(next);
  };

  const lines = Object.entries(units)
    .map(([roomCategoryId, value]) => ({ roomCategoryId: Number(roomCategoryId), units: Number(value || 0) }))
    .filter((line) => line.units > 0);

  const submit = async (event) => {
    event.preventDefault();
    if (!startDate || !endDate || endDate <= startDate) {
      toast.error("Choose check-in and check-out");
      return;
    }
    if (!lines.length) {
      toast.error("Enter how many rooms to hold for at least one room type");
      return;
    }
    setSaving(true);
    try {
      await createRoomHold({
        hotelId: hotel.hotelId,
        startDate,
        endDate,
        note: note.trim() || null,
        confirmedByUs,
        lines,
      });
      toast.success("Rooms held for Iotrips");
      await onCreated?.();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to hold rooms");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={Boolean(hotel)} onHide={() => !saving && onHide?.()} centered>
      <Form onSubmit={submit}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>
            Hold Rooms for Iotrips
            {hotel && <div className="small text-muted fw-normal">{hotel.hotelCode}{hotel.city ? ` · ${hotel.city}` : ""}</div>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={6}>
              <Form.Label>Check-in</Form.Label>
              <IsoDatePicker min={todayValue()} max={addDays(todayValue(), 89)} value={startDate} onChange={setStartDate} />
            </Col>
            <Col xs={6}>
              <Form.Label>Check-out</Form.Label>
              <IsoDatePicker min={startDate ? addDays(startDate, 1) : addDays(todayValue(), 1)} max={addDays(todayValue(), 90)} value={endDate} onChange={setEndDate} />
            </Col>
            {nights.length > 0 && <Col xs={12} className="small text-muted mt-0">{nightsLabel(nights.length)}</Col>}
            <Col xs={12}>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <Form.Label className="mb-0">Rooms to hold</Form.Label>
                <Button size="sm" variant="link" className="p-0" onClick={holdWholeHotel} disabled={!nights.length}>
                  Hold every free room
                </Button>
              </div>
              <div className="border rounded p-2 d-flex flex-column gap-2">
                {categories.map((category) => {
                  const free = freeRooms(category);
                  return (
                    <div key={category.roomCategoryId} className="d-flex align-items-center justify-content-between gap-2">
                      <div>
                        <div>{category.categoryName}</div>
                        <div className="small text-muted">
                          {free == null ? `${category.totalUnits} rooms` : `${free} free on every night`}
                        </div>
                      </div>
                      <Form.Control
                        type="number"
                        min="0"
                        max={free == null ? category.totalUnits : free}
                        style={{ width: 90 }}
                        value={units[category.roomCategoryId] ?? ""}
                        placeholder="0"
                        onChange={(event) => setUnits((current) => ({ ...current, [category.roomCategoryId]: event.target.value }))}
                        aria-label={`Rooms of ${category.categoryName} to hold`}
                      />
                    </div>
                  );
                })}
                {!categories.length && <div className="small text-muted">This hotel has no active room types.</div>}
              </div>
            </Col>
            <Col xs={12}>
              <Form.Label>Note</Form.Label>
              <Form.Control value={note} maxLength={255} placeholder="e.g. Diwali long weekend" onChange={(event) => setNote(event.target.value)} />
            </Col>
            <Col xs={12}>
              <Form.Check
                type="checkbox"
                id="hold-confirmed-by-us"
                label="The hotel has already agreed (phone / WhatsApp)"
                checked={confirmedByUs}
                onChange={(event) => setConfirmedByUs(event.target.checked)}
              />
              <div className="small text-muted">
                The hold starts now either way. Otherwise it shows as awaiting the hotel until they accept in their portal.
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
          <Button type="submit" disabled={saving || !lines.length || !nights.length}>
            {saving ? <Spinner animation="border" size="sm" /> : `Hold ${lines.reduce((total, line) => total + line.units, 0) || ""} Room${lines.reduce((total, line) => total + line.units, 0) === 1 ? "" : "s"}`}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
